import type { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import type { Batch } from '../../../models/Batch';
import BlockBatchModal from './BlockBatchModal';
import CreateBatchModal from './CreateBatchModal';
import DeleteBatchModal from './DeleteBatchModal';
import EditBatchModal from './EditBatchModal';
import UnblockBatchModal from './UnblockBatchModal';

interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithBatch: (batch: Batch) => void;
}

export interface BatchesModalsRef {
  openCreate: () => void;
  openEdit: (batch: Batch) => void;
  openDelete: (batch: Batch) => void;
  openBlock: (batch: Batch) => void;
  openUnblock: (batch: Batch) => void;
}

const BatchesModals = forwardRef<BatchesModalsRef, object>((_, ref) => {
  const createRef = useRef<ModalCenterRef>(null);
  const editRef = useRef<ExtendedModalCenterRef>(null);
  const deleteRef = useRef<ExtendedModalCenterRef>(null);
  const blockRef = useRef<ExtendedModalCenterRef>(null);
  const unblockRef = useRef<ExtendedModalCenterRef>(null);

  useImperativeHandle(
    ref,
    () => ({
      openCreate: () => createRef.current?.openModal(),
      openEdit: (batch) => editRef.current?.openWithBatch(batch),
      openDelete: (batch) => deleteRef.current?.openWithBatch(batch),
      openBlock: (batch) => blockRef.current?.openWithBatch(batch),
      openUnblock: (batch) => unblockRef.current?.openWithBatch(batch),
    }),
    [],
  );

  return (
    <>
      <CreateBatchModal ref={createRef} />
      <EditBatchModal ref={editRef} />
      <DeleteBatchModal ref={deleteRef} />
      <BlockBatchModal ref={blockRef} />
      <UnblockBatchModal ref={unblockRef} />
    </>
  );
});

BatchesModals.displayName = 'BatchesModals';

export default BatchesModals;
